<template>
  <div class="payment-form-container-new bg-white rounded-3 shadow-sm overflow-hidden">
    <div class="row g-0" v-if="!isLoading">
      <!-- Columna Izquierda: Detalles del Pago (Fondo blanco) -->
      <div class="col-12 col-lg-7 p-4">
        <!-- Detalles de inscripción y banco destino -->
        <InscriptionAndBankDetails 
          :course-name="courseName"
          :bcv-rate="bcvRateValue"
          :total-paid="totalPaid"
          :total-pending="totalPending"
          :bank-details="modelValue.selectedCompanyBank"
          :payment-method="modelValue.paymentMethod"
        />
        
        <!-- Selección de conceptos / cuotas -->
        <PaymentConceptsSelector 
          :concepts="concepts"
          v-model="selectedInstallments"
          :loading="loadingOffer"
          :total-paid="totalPaid"
        />
      </div>
      
      <!-- Columna Derecha: Formulario y Resumen (Fondo azul/gris claro) -->
      <div class="col-12 col-lg-5 border-start">
        <PaymentSummarySidebar
          ref="summarySidebarRef"
          :model-value="modelValue"
          @update:model-value="$emit('update:modelValue', $event)"
          :company-bank="companyBank"
          :banks-nationals="banksNationals"
          :bcv-rate="bcvRateValue"
          :subtotal-u-s-d="subtotalUSD"
          :total-payable-bs="totalPayableBs"
          @submit="(file) => $emit('submit', file)"
          @cancel="$emit('cancel')"
        />
      </div>
    </div>
    
    <!-- Skeleton Loading principal que imita la estructura de 2 columnas -->
    <PaymentReportFormSkeleton v-else />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { parseEsNumber } from 'src/utils/numberUtils.js';

import InscriptionAndBankDetails from './components/InscriptionAndBankDetails.vue';
import PaymentConceptsSelector from './components/PaymentConceptsSelector.vue';
import PaymentSummarySidebar from './components/PaymentSummarySidebar.vue';
import PaymentReportFormSkeleton from './components/PaymentReportFormSkeleton.vue';

import { useBcvRateAPI } from 'src/composables/api/useBcvRateAPI.js';
import { useCompanyBankAPI } from 'src/composables/api/useCompanyBankAPI.js';
import { useTypeBanksAPI } from 'src/composables/api/useTypeBanksAPI.js';

const store = useStore();

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  courseName: {
    type: String,
    default: ''
  },
  fallbackAmount: {
    type: [Number, String],
    default: 0
  },
  idPlatformOffer: {
    type: [Number, String],
    default: null
  },
  idPlatform: {
    type: [Number, String],
    default: null
  },
  totalPaid: {
    type: [Number, String],
    default: 0
  },
  totalPending: {
    type: [Number, String],
    default: 0
  }
});

const emit = defineEmits(['update:modelValue', 'update:bcvRate', 'submit', 'cancel']);

const summarySidebarRef = ref(null);
const companyBank = ref([]);
const banksNationals = ref([]);
const bcvRateData = ref({});
const isLoading = ref(true);

const offerData = ref(null);
const loadingOffer = ref(false);
const selectedInstallments = ref([]); // Lista de IDs de conceptos seleccionados

const { getCurrentBcvRate } = useBcvRateAPI();
const { getCompanyBanks } = useCompanyBankAPI();
const { getTypeBanks } = useTypeBanksAPI();

// Valor numérico de la tasa BCV
const bcvRateValue = computed(() => {
  return parseEsNumber(bcvRateData.value?.value || 0);
});

// Cargar detalles de la oferta de la plataforma
const loadOfferDetails = async () => {
  if (!props.idPlatform) return;
  loadingOffer.value = true;
  try {
    const response = await store.dispatch('getData', {
      url: "platform_offer/all",
      params: {
        id_platform: props.idPlatform,
        active: 1
      }
    });
    if (response.code === 200 && response.data && Array.isArray(response.data)) {
      // Buscar la oferta que corresponde al id_platform_offer de la postulación
      const foundOffer = response.data.find(offer => Number(offer.id) === Number(props.idPlatformOffer)) || response.data[0];
      if (foundOffer) {
        offerData.value = foundOffer;
        
        // Seleccionar por defecto el primer concepto que no esté pagado
        await nextTick();
        const firstPending = concepts.value.find(c => c.paidStatus !== 'paid');
        selectedInstallments.value = firstPending ? [firstPending.id] : [];
      }
    }
  } catch (error) {
    console.error('Error loading platform offer:', error);
  } finally {
    loadingOffer.value = false;
  }
};

// Construir la lista unificada de conceptos seleccionables (Inscripción y Cuotas)
const concepts = computed(() => {
  const list = [];
  
  const currentOfferData = offerData.value;
  
  if (currentOfferData) {
    // 1. Agregar Inscripción si corresponde
    if (Number(currentOfferData.check_enrollment) === 1 && parseEsNumber(currentOfferData.enrollment_amount) > 0) {
      list.push({
        id: 'enrollment',
        name: 'Inscripción',
        amount: parseEsNumber(currentOfferData.enrollment_amount),
        type: 'enrollment'
      });
    }
    
    // 2. Agregar Cuotas/Installments
    if (currentOfferData.installments && currentOfferData.installments.length > 0) {
      currentOfferData.installments.forEach((inst, index) => {
        list.push({
          id: `installment_${inst.id}`,
          name: `Cuota #${index + 1}`,
          amount: parseEsNumber(inst.amount),
          dueDate: inst.due_date,
          type: 'installment'
        });
      });
    }
  }
  
  // Fallback si no hay data de oferta cargada todavía o si la lista de arriba resultó vacía
  if (list.length === 0) {
    list.push({
      id: 'fallback_inscription',
      name: 'Inscripción',
      amount: parseEsNumber(props.fallbackAmount || 0),
      type: 'enrollment'
    });
  }
  
  const paidAmount = Number(props.totalPaid || 0);
  
  // Si el abono es 0, no aplicar lógica de abonos
  if (paidAmount <= 0) {
    return list.map(item => ({
      ...item,
      paidStatus: 'pending',
      remainingAmount: item.amount,
      allocatedAmount: 0
    }));
  }

  let remainingPaid = paidAmount;

  return list.map(item => {
    const cost = item.amount;
    let status = 'pending';
    let remaining = cost;
    let allocated = 0;

    if (remainingPaid > 0) {
      if (remainingPaid >= cost) {
        allocated = cost;
        remainingPaid -= cost;
        status = 'paid';
        remaining = 0;
      } else {
        allocated = remainingPaid;
        remainingPaid = 0;
        status = 'partial';
        remaining = cost - allocated;
      }
    }

    return {
      ...item,
      paidStatus: status,
      remainingAmount: remaining,
      allocatedAmount: allocated
    };
  });
});

// Subtotal en USD según conceptos seleccionados
const subtotalUSD = computed(() => {
  return concepts.value
    .filter(item => selectedInstallments.value.includes(item.id))
    .reduce((sum, item) => sum + item.remainingAmount, 0);
});

// Total a pagar en Bolívares (USD * Tasa BCV)
const totalPayableBs = computed(() => {
  return subtotalUSD.value * bcvRateValue.value;
});

const updateField = (field, value) => {
  emit('update:modelValue', {
    ...props.modelValue,
    [field]: value
  });
};

// Sincronizar el campo de entrada de monto automáticamente según la moneda seleccionada
watch([subtotalUSD, totalPayableBs, () => props.modelValue.currency], () => {
  const finalAmount = props.modelValue.currency === 'USD' ? subtotalUSD.value : totalPayableBs.value;
  updateField('amountWithPrefix', finalAmount);
}, { immediate: true });

onMounted(async () => {
  const [bcvResult, companyBankResult, banksNationalsResult] = await Promise.all([
    getCurrentBcvRate(),
    getCompanyBanks(),
    getTypeBanks()
  ]);
  
  bcvRateData.value = bcvResult;
  companyBank.value = companyBankResult;
  banksNationals.value = banksNationalsResult;
  
  emit('update:bcvRate', bcvRateData.value);
  
  await loadOfferDetails();
  
  isLoading.value = false;
});

const getPaymentFiles = () => {
  return summarySidebarRef.value?.getFiles() || [];
};

defineExpose({
  getPaymentFiles
});
</script>

<style scoped>
.payment-form-container-new {
  border: 1px solid #e2e8f0;
  zoom: 0.85;
}
</style>
